import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Global analysis scope shared by every page:
 *   scope  - 'all' | 'cluster:<id>' | '<site_id>'
 *   period - wow | last_week | mom | last_month | last7 | last30 | last90 | all | custom
 *   range  - { start, end } when period === 'custom'
 *   overlay- holiday overlay on charts (on/off)
 */
const KEY = "zapin.scope.v2";
const ScopeContext = createContext(null);

const load = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    /* ignore */
  }
  return null;
};

export const PERIOD_TABS = [
  { id: "wow", label: "Week on week", short: "WoW" },
  { id: "mom", label: "Month on month", short: "MoM" },
  { id: "custom", label: "Custom range", short: "Custom" },
];

export const PERIOD_PRESETS = [
  { id: "wow", label: "This week vs last week" },
  { id: "last_week", label: "Last full week" },
  { id: "mom", label: "This month vs last month" },
  { id: "last_month", label: "Last full month" },
  { id: "last7", label: "Last 7 days" },
  { id: "last30", label: "Last 30 days" },
  { id: "last90", label: "Last 90 days" },
  { id: "all", label: "Everything on file" },
];

export const ScopeProvider = ({ children }) => {
  const saved = load() || {};
  const [scope, setScope] = useState(saved.scope || "all");
  const [period, setPeriod] = useState(saved.period || "wow");
  const [range, setRange] = useState(saved.range || null);
  const [overlay, setOverlay] = useState(saved.overlay ?? true);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify({ scope, period, range, overlay }));
  }, [scope, period, range, overlay]);

  const setCustomRange = useCallback((start, end) => {
    setRange({ start, end });
    setPeriod("custom");
  }, []);

  const params = useMemo(() => {
    const p = { scope, period };
    if (period === "custom" && range?.start && range?.end) {
      p.start = range.start;
      p.end = range.end;
    } else if (period === "custom") {
      p.period = "last30";
    }
    return p;
  }, [scope, period, range]);

  const value = useMemo(() => ({ scope, setScope, period, setPeriod, range, setCustomRange, overlay, setOverlay, params }), [scope, period, range, setCustomRange, overlay, params]);
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
};

export const useScope = () => {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error("useScope must be used inside ScopeProvider");
  return ctx;
};
